import { useEffect, useState } from "react";
import { api } from "@shared/api";
import { Banner, Field } from "@shared/ui";
import { MediaPicker } from "./MediaPicker";
import {
  PLATFORM_LABELS,
  formatDayKey,
  formatTimeIn,
  type Post,
  type Variant,
} from "@shared/types";

/**
 * Editing one post across its channels. Shown as a panel over the week view so
 * the surrounding plan stays visible.
 */
export function PostEditor({
  post,
  timezone,
  workspaceId,
  onClose,
  onChanged,
}: {
  post: Post;
  timezone: string;
  workspaceId: string;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const [activeId, setActiveId] = useState(post.variants[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [instruction, setInstruction] = useState("");

  const active = post.variants.find((v) => v.id === activeId) ?? post.variants[0];

  // Close on Escape, which is what people expect from a panel like this.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function regenerate(scope: "all" | "one") {
    if (!instruction.trim()) {
      setError("Say what should change first.");
      return;
    }
    setBusy("regenerate");
    setError(null);
    try {
      await api.post(`/api/admin/posts/${post.id}/regenerate`, {
        instruction,
        channelIds: scope === "one" && active ? [active.channelId] : [],
      });
      setInstruction("");
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not rewrite the post");
    } finally {
      setBusy(null);
    }
  }

  async function reschedule(dayKey: string, hour: number, minute: number) {
    setBusy("schedule");
    setError(null);
    try {
      await api.patch(`/api/admin/posts/${post.id}/schedule`, { dayKey, hour, minute });
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not move the post");
    } finally {
      setBusy(null);
    }
  }

  const time = formatTimeIn(post.scheduledAt, timezone);
  const [hh, mm] = time.split(":").map(Number);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        className="drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Edit post"
      >
        <div className="drawer-head">
          <div>
            <h2>{post.brief || "Post"}</h2>
            <p className="muted" style={{ marginTop: 4 }}>
              {formatDayKey(post.dayKey)} at {time} · revision {post.revision} · {post.locale}
            </p>
          </div>
          <button className="small" onClick={onClose}>
            Close
          </button>
        </div>

        {error ? <Banner kind="error">{error}</Banner> : null}

        <div className="tabs">
          {post.variants.map((v) => (
            <button
              key={v.id}
              className={v.id === active?.id ? "tab active" : "tab"}
              onClick={() => setActiveId(v.id)}
            >
              {PLATFORM_LABELS[v.platform] ?? v.platform}
              {v.warning ? " ⚠" : ""}
            </button>
          ))}
        </div>

        {active ? (
          <>
            <VariantEditor
              key={active.id}
              postId={post.id}
              variant={active}
              onSaved={onChanged}
            />
            <MediaPicker
              key={`media-${active.id}`}
              workspaceId={workspaceId}
              platform={active.platform}
              selected={active.mediaIds}
              suggestedPrompt={
                typeof active.options.imagePrompt === "string"
                  ? active.options.imagePrompt
                  : undefined
              }
              onChange={async (mediaIds) => {
                await api.put(`/api/admin/variants/${active.id}/media`, { mediaIds });
                await onChanged();
              }}
            />
          </>
        ) : null}

        <div className="card" style={{ marginTop: 14 }}>
          <h3 style={{ marginBottom: 10 }}>Rewrite with AI</h3>
          <Field label="What should change?">
            <textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Shorter. Drop the question at the end. Mention the opening hours."
              style={{ minHeight: 70 }}
            />
          </Field>
          <div className="actions">
            <button
              className="primary"
              onClick={() => void regenerate("all")}
              disabled={busy !== null}
            >
              {busy === "regenerate" ? "Rewriting…" : "Rewrite all channels"}
            </button>
            <button onClick={() => void regenerate("one")} disabled={busy !== null || !active}>
              Only {active ? (PLATFORM_LABELS[active.platform] ?? active.platform) : "this one"}
            </button>
          </div>
          {post.feedback.filter((f) => !f.resolvedAt).length > 0 ? (
            <p className="muted" style={{ marginTop: 10 }}>
              {post.feedback.filter((f) => !f.resolvedAt).length} unresolved client note
              {post.feedback.filter((f) => !f.resolvedAt).length === 1 ? "" : "s"} will be
              included in the rewrite.
            </p>
          ) : null}
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <h3 style={{ marginBottom: 10 }}>When it posts</h3>
          <div className="row">
            <Field label="Day">
              <input
                type="date"
                value={post.dayKey}
                onChange={(e) => void reschedule(e.target.value, hh ?? 9, mm ?? 0)}
                disabled={busy !== null}
              />
            </Field>
            <Field label="Time" hint={`Local time in ${timezone}.`}>
              <input
                type="time"
                value={time}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(":").map(Number);
                  if (Number.isFinite(h) && Number.isFinite(m)) {
                    void reschedule(post.dayKey, h!, m!);
                  }
                }}
                disabled={busy !== null}
              />
            </Field>
          </div>
        </div>

        {post.feedback.length > 0 ? (
          <div className="card" style={{ marginTop: 12 }}>
            <h3 style={{ marginBottom: 10 }}>Client notes</h3>
            <ul className="list">
              {post.feedback.map((f) => (
                <li key={f.id} className="list-item">
                  <div>
                    <div>{f.body}</div>
                    <div className="muted">{f.authorLabel}</div>
                  </div>
                  <span className={`pill ${f.resolvedAt ? "ok" : "warn"}`}>
                    {f.resolvedAt ? "handled" : "open"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function VariantEditor({
  postId,
  variant,
  onSaved,
}: {
  postId: string;
  variant: Variant;
  onSaved: () => Promise<void> | void;
}) {
  const [body, setBody] = useState(variant.body);
  const [hashtags, setHashtags] = useState(variant.hashtags.join(" "));
  const [title, setTitle] = useState(variant.title ?? "");
  const [firstComment, setFirstComment] = useState(variant.firstComment ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const tags = hashtags.split(/[\s,]+/).map((t) => t.replace(/^#+/, "")).filter(Boolean);
  // Instagram puts hashtags in the first comment, so they do not count here.
  const countsHashtags = variant.platform !== "instagram";
  const total = body.length + (countsHashtags && tags.length ? tags.join(" ").length + tags.length + 2 : 0);
  const over = variant.hardLimit != null && total > variant.hardLimit;

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await api.patch(`/api/admin/posts/${postId}/variants/${variant.id}`, {
        body,
        hashtags: tags,
        title: title || null,
        firstComment: firstComment || null,
      });
      setSaved(true);
      await onSaved();
    } finally {
      setBusy(false);
    }
  }

  const dirty =
    body !== variant.body ||
    tags.join(" ") !== variant.hashtags.join(" ") ||
    title !== (variant.title ?? "") ||
    firstComment !== (variant.firstComment ?? "");

  return (
    <div className="card">
      {variant.requiresMedia && variant.mediaIds.length === 0 ? (
        <Banner kind="warn">
          {PLATFORM_LABELS[variant.platform] ?? variant.platform} will not accept a post
          without an image or video. Add one before this can publish.
        </Banner>
      ) : null}

      {variant.platform === "youtube" ? (
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} />
        </Field>
      ) : null}

      <Field label="Post text">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 190 }} />
      </Field>

      <p className={over ? "count over" : "count"}>
        {total} characters
        {variant.hardLimit ? ` of ${variant.hardLimit}` : ""}
        {variant.target ? ` · aim for ${variant.target[0]} to ${variant.target[1]}` : ""}
        {over ? " · too long to publish" : ""}
      </p>

      <Field label="Hashtags" hint="Space separated. The hash is added automatically.">
        <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} />
      </Field>

      {variant.platform === "instagram" ? (
        <Field label="First comment" hint="Where the hashtags go on Instagram.">
          <textarea
            value={firstComment}
            onChange={(e) => setFirstComment(e.target.value)}
            style={{ minHeight: 60 }}
          />
        </Field>
      ) : null}

      {typeof variant.options.imagePrompt === "string" ? (
        <p className="muted" style={{ marginBottom: 12 }}>
          Suggested image: {variant.options.imagePrompt}
        </p>
      ) : null}

      <div className="actions">
        <button className="primary" onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save changes"}
        </button>
        {saved && !dirty ? <span className="muted">Saved.</span> : null}
      </div>
    </div>
  );
}
